import React, { useEffect, useState } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Header, Footer, SEO } from '../../../shared/components';
import { useServices } from '../../../shared/hooks/useServices';
import { config } from '../../auth/config';

const APP_NAME = import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners';
const LEGAL_NAME = 'Kuthati Sai Ram';
const ADDRESS = 'HNO 33-5-202, Satyasai Nagar, Opp Naidu Pump, Warangal, Telangana - 506002';
const EMAIL = 'rrdplotmagic@gmail.com';
const PHONE = '+91 9573766436';

interface CreditPack {
  name: string;
  credits: number;
  priceInr: number;
}

const AboutPage: React.FC = () => {
  const { services, loading } = useServices();
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);

  useEffect(() => {
    fetch(`${config.api.baseUrl}/api/payment/packs`, {
      headers: { [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue },
    })
      .then(r => r.json())
      .then(json => {
        const data = json.data?.packs || json.data || [];
        setPacks(data.map((p: any) => ({ name: p.name, credits: p.credits, priceInr: p.priceInr ?? p.price ?? 0 })));
      })
      .catch(() => {})
      .finally(() => setPacksLoading(false));
  }, []);
  return (
    <PageLayout size="full">
      <SEO title={`About Us - ${APP_NAME}`} description="Learn about our mission and services" />
      <Header />
      <PageLayout.Content>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold text-foreground">About Us</h1>
              <p className="text-muted-foreground">Professional plot map generation made simple</p>
            </div>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Who We Are</h2>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>{APP_NAME}</strong> is an online Software-as-a-Service (SaaS) platform operated by <strong>{LEGAL_NAME}</strong>, based in Warangal, Telangana, India. We provide a web-based tool that enables surveyors, document writers, real estate agents, and construction professionals to generate professional-quality cadastral plot maps as downloadable PDF documents.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Nature of Business</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We are a <strong>digital services platform</strong> where registered users pay on demand per download to generate and download A4-sized, print-ready plot map PDFs from our web application. All products delivered are <strong>digital in nature</strong> — there are no physical goods, shipping, or inventory involved.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>Why we need a payment gateway:</strong> Customers pay per download (priced in INR) each time they generate and download a plot map PDF. A payment gateway is required to securely process these online payments via UPI, debit cards, credit cards, and net banking.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">Our platform offers the following services:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>Regular Plot Maps</strong> - Square and rectangular plot maps with precise dimensions</li>
                  <li><strong>Irregular Plot Maps</strong> - Trapezoid and irregular shapes using surveyor-standard trilateration</li>
                  <li><strong>Room Layouts</strong> - Plot maps with ACC roof house room layouts included</li>
                  <li><strong>Professional Accuracy</strong> - Precise area calculations with exact edge lengths</li>
                  <li><strong>Instant PDF Downloads</strong> - Print-ready A4 documents with professional formatting</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Our Mission</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To make professional-quality cadastral plot map generation accessible to every surveyor, document writer, and real estate professional across India — without requiring expensive specialized software or training. We believe accurate documentation should be affordable, fast, and available to everyone.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">How It Works</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                  {[
                    { step: '1', title: 'Enter Dimensions', desc: 'Input your plot measurements in feet and inches' },
                    { step: '2', title: 'Customize Details', desc: 'Add vendor/vendee info, boundaries, and labels' },
                    { step: '3', title: 'Preview & Adjust', desc: 'Fine-tune positioning, text sizes, and layout' },
                    { step: '4', title: 'Download PDF', desc: 'Get your print-ready A4 plot map instantly' },
                  ].map(item => (
                    <div key={item.step} style={{ background: '#141414', border: '1px solid #222', borderRadius: 12, padding: 20 }}>
                      <div style={{ width: 32, height: 32, background: 'rgba(255,217,61,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFD93D', fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
                        {item.step}
                      </div>
                      <h3 style={{ color: '#e5e5e5', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{item.title}</h3>
                      <p style={{ color: '#666', fontSize: 13 }}>{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Pricing</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use a simple credit-based system. Purchase credit packs and use them to download plot maps. All prices are in Indian Rupees (INR):
                </p>
                <div style={{ background: '#141414', border: '1px solid #222', borderRadius: 12, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #222' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', color: '#888', fontWeight: 600 }}>Service</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', color: '#888', fontWeight: 600 }}>New Plan</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', color: '#888', fontWeight: 600 }}>Edit/Re-download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: '#666' }}>Loading pricing…</td>
                        </tr>
                      )}
                      {!loading && services.length === 0 && (
                        <tr>
                          <td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: '#666' }}>Pricing unavailable</td>
                        </tr>
                      )}
                      {!loading && services.map(svc => (
                        <tr key={svc.code} style={{ borderBottom: '1px solid #1a1a1a' }}>
                          <td style={{ padding: '10px 16px', color: '#ccc' }}>{svc.name}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#FFD93D', fontWeight: 600 }}>&#8377;{svc.tier1Price}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#888' }}>&#8377;{svc.tier2Price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Credit Packs</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Top up your wallet with a credit pack. All packs include up to <strong style={{ color: '#FFD93D' }}>10% discount</strong> — you get more credits than you pay for:
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                  {packsLoading && [1,2,3,4].map(i => (
                    <div key={i} style={{ background: '#141414', borderRadius: 12, padding: 20, height: 100 }} className="animate-pulse" />
                  ))}
                  {!packsLoading && packs.map(pack => {
                    const bonus = pack.credits - pack.priceInr;
                    const bonusPct = pack.priceInr > 0 ? Math.round((bonus / pack.priceInr) * 100) : 0;
                    return (
                      <div key={pack.name} style={{ background: '#141414', border: '1px solid #222', borderRadius: 12, padding: '20px 16px', textAlign: 'center' }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#FFD93D', margin: '0 0 6px' }}>{pack.name}</p>
                        <p style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: '0 0 2px' }}>₹{pack.priceInr}</p>
                        <p style={{ fontSize: 14, color: '#ccc', fontWeight: 700, margin: '0 0 4px' }}>{pack.credits} credits</p>
                        {bonus > 0 && (
                          <p style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, margin: 0 }}>+{bonus} bonus ({bonusPct}%)</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Payment Methods</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We accept all major payment methods through our secure payment gateway:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                  <li>UPI (Google Pay, PhonePe, Paytm, etc.)</li>
                  <li>Credit & Debit Cards (Visa, Mastercard, RuPay)</li>
                  <li>Net Banking (all major Indian banks)</li>
                  <li>Wallets (Paytm, Mobikwik, etc.)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Contact Information</h2>
                <div style={{ background: '#141414', border: '1px solid #222', borderRadius: 12, padding: 24 }}>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">{LEGAL_NAME}</strong><br />
                    {ADDRESS}
                  </p>
                  <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p className="text-muted-foreground">
                      Email: <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a>
                    </p>
                    <p className="text-muted-foreground">
                      Phone: <a href={`tel:${PHONE}`} className="text-primary hover:underline">{PHONE}</a>
                    </p>
                  </div>
                </div>
              </section>
            </div>

            <div className="text-center pt-8 border-t border-border">
              <a href="/" className="inline-flex items-center text-primary hover:underline font-medium">← Back to Home</a>
            </div>
          </div>
        </div>
      </PageLayout.Content>
      <Footer />
    </PageLayout>
  );
};

export default AboutPage;
